import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { DomainConfigurations } from '../../viewmodels/configuration/domain-configurations';
import { SystemSetting } from '../../viewmodels/configuration/system-setting';
import { ConfigService } from '../config.service';
import { Domain } from '../domain';
import { DomainConfigurationService } from './domain-configuration.service';
import { SystemConfigurationSettingNames } from './system-configuration-setting-names';
import { SystemConfigurationService } from './system-configuration.service';

/**
 * Application-wide system configuration settings
 */
@Injectable()
export class SystemSettings {

  private _defaultDomain: Domain = Domain.Energy;
  private _domainConfiguration: DomainConfigurations;

  public domainChanged = new Subject<Domain>();

  public get domain(): Domain {
    const domainName = this.configService.domain.getValue();
    return this.getDomainByName(domainName);
  }

  public get domainConfiguration(): DomainConfigurations {
    return this._domainConfiguration;
  }

  constructor(
    private systemConfigService: SystemConfigurationService, 
    private domainConfigService: DomainConfigurationService,
    private configService: ConfigService
  ) { }

  public initialiseSettings() {
    this.systemConfigService
      .getAllSystemConfigurationSettings().pipe(
        take(1))
      .subscribe(settings => {
        if (settings) {
          settings.forEach(x => this.mapSetting(x));
        }
      });

    this.domainConfigService
      .getConfiguration().pipe(
        take(1))
      .subscribe(config => {
        if (config) {
          this._domainConfiguration = config;
        }
      });
  }

  private mapSetting(setting: SystemSetting) {
    switch (setting.name) {
    case SystemConfigurationSettingNames.Domain:
      const domain = this.getDomainByName(setting.value);
      if (this.isDomainChanged(domain)) {
        this.configService.setDomain(domain);
        this.domainChanged.next(domain);
      }
      break;
    default:
      console.error('Invalid System Configuration Setting: ' + setting.name);
      break;
    }
  }

  private getDomainByName(domainName: string): Domain {
    return Object.keys(Domain).includes(domainName) ? Domain[domainName] : this._defaultDomain;
  }

  private isDomainChanged(newDomain: Domain) {
    const currentDomain = this.domain;
    return newDomain !== currentDomain;
  }
}
